import { createFileRoute } from "@tanstack/react-router";
import { Download, FileSpreadsheet, ShieldCheck } from "lucide-react";
import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { formatINR, sampleDonations } from "@/project/data/sample";
import { partners, payouts, platformFee, campaigns, auditLog } from "@/project/data/network";
import { downloadCsv } from "@/project/lib/receipt";

export const Route = createFileRoute("/institution/payouts")({ component: OrgPayouts });

function OrgPayouts() {
  const org = partners[0];
  const mine = payouts.filter((p) => p.orgId === org.id);
  const paid = mine.filter((p) => p.status === "Paid").reduce((s, p) => s + p.net, 0);
  const myDonations = sampleDonations;
  const myCampaigns = campaigns.filter((c) => c.orgId === org.id).length ? campaigns.filter((c) => c.orgId === org.id) : campaigns.slice(0, 4);
  const collected = myDonations.filter((d) => d.status === "Success").reduce((s, d) => s + d.amount, 0);
  const settledGross = mine.filter((p) => p.status === "Paid").reduce((s, p) => s + p.gross, 0);
  const unreconciled = collected - settledGross;

  const exportPayouts = () =>
    downloadCsv(`settlements-${org.id}.csv`, [
      ["Payout ID", "Period", "Gross", "Platform fee", "Net", "Status", "UTR", "Value date"],
      ...mine.map((p) => [p.id, p.period, p.gross, p.fee, p.net, p.status, p.utr ?? "", p.date]),
    ]);

  const exportDonations = () =>
    downloadCsv(`donations-${org.id}.csv`, [
      ["Donation ID", "Date", "Donor", "Cause", "Organisation", "Amount", "Status"],
      ...myDonations.map((d) => [d.id, d.date, d.donor, d.cause, d.institution, d.amount, d.status]),
    ]);

  const exportCampaigns = () =>
    downloadCsv(`campaign-report-${org.id}.csv`, [
      ["Campaign ID", "Title", "Domain", "Goal", "Raised", "% funded", "Donors", "Status"],
      ...myCampaigns.map((c) => [c.id, c.title, c.domain, c.goal, c.raised, `${Math.round((c.raised / c.goal) * 100)}%`, c.donors ?? "", c.status]),
    ]);

  const exportAudit = () =>
    downloadCsv(`audit-trail-${org.id}.csv`, [
      ["Entry", "Actor", "Action", "Target", "Timestamp"],
      ...auditLog.map((a) => [a.id, a.actor, a.action, a.target, a.at]),
    ]);

  return (
    <>
      <PageHead title="Settlements" subtitle="Payout cycles, platform fee and bank references for your organisation." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Settled to bank" value={formatINR(paid)} tone="good" />
        <StatCard label="Pending payout" value={formatINR(org.payoutPending)} tone="warn" />
        <StatCard label="Platform fee" value={`${platformFee.standard}%`} />
        <StatCard label="Compliance score" value={`${org.complianceScore}/100`} tone="primary" />
      </div>

      <div className="mt-6">
        <Panel title="Audit exports">
          <p className="mb-4 text-sm text-foreground/65">
            Download the four registers your auditor needs. Every file is generated from the same ledger, so
            settlement totals reconcile line-by-line with donations and campaign reports.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ExportBtn label="Settlement register" hint={`${mine.length} payout cycles`} onClick={exportPayouts} />
            <ExportBtn label="Donation ledger" hint={`${myDonations.length} donations`} onClick={exportDonations} />
            <ExportBtn label="Campaign report" hint={`${myCampaigns.length} campaigns`} onClick={exportCampaigns} />
            <ExportBtn label="Audit trail" hint={`${auditLog.length} actions`} onClick={exportAudit} />
          </div>
        </Panel>
      </div>

      <div className="mt-6">
        <Panel title="Reconciliation">
          <div className="grid gap-4 sm:grid-cols-3">
            <Recon label="Donations collected" value={formatINR(collected)} note="Successful donations in the ledger" />
            <Recon label="Gross settled" value={formatINR(settledGross)} note="Paid payout cycles" />
            <Recon
              label={unreconciled === 0 ? "Fully reconciled" : "Awaiting settlement"}
              value={formatINR(Math.abs(unreconciled))}
              note={unreconciled === 0 ? "No open difference" : "Collected but not yet paid out"}
            />
          </div>
          <p className="mt-4 flex items-center gap-2 rounded-xl bg-[var(--cream-2)] px-4 py-3 text-xs text-foreground/70">
            <ShieldCheck className="h-4 w-4 text-[var(--success)]" />
            Platform fee of {platformFee.standard}% is deducted per cycle. {platformFee.note}
          </p>
        </Panel>
      </div>

      <div className="mt-6">
        <Panel title="Payout history" action={<button onClick={exportPayouts} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs"><Download className="h-3 w-3" /> Statement</button>}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-xs uppercase tracking-wider text-foreground/70">
                <tr className="border-b border-border text-left"><th className="py-2">Payout</th><th>Period</th><th>Gross</th><th>Fee</th><th>Net</th><th>Status</th><th>UTR</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {mine.map((p) => (
                  <tr key={p.id}>
                    <td className="py-3 font-mono text-xs">{p.id}</td>
                    <td>{p.period}</td>
                    <td>{formatINR(p.gross)}</td>
                    <td className="text-foreground/60">{formatINR(p.fee)}</td>
                    <td className="font-semibold text-[var(--maroon)]">{formatINR(p.net)}</td>
                    <td>{p.status}</td>
                    <td className="font-mono text-xs">{p.utr ?? "—"}</td>
                  </tr>
                ))}
                {mine.length === 0 && <tr><td colSpan={7} className="py-4 text-foreground/60">No settlements yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}

function ExportBtn({ label, hint, onClick }: { label: string; hint: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-3 rounded-xl border border-border bg-background p-4 text-left transition hover:border-[var(--maroon)]/40 hover:bg-[var(--cream-2)]"
    >
      <FileSpreadsheet className="mt-0.5 h-4 w-4 text-[var(--maroon)]" />
      <span>
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-xs text-foreground/70">{hint} · CSV</span>
      </span>
    </button>
  );
}

function Recon({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background p-4">
      <p className="text-xs uppercase tracking-wider text-foreground/70">{label}</p>
      <p className="mt-1 font-display text-xl font-bold text-[var(--maroon)]">{value}</p>
      <p className="mt-1 text-xs text-foreground/70">{note}</p>
    </div>
  );
}